import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface SystemSettings {
  // Branding
  platform_name: string;
  platform_slogan: string;
  logo_url: string;
  logo_small_url: string;
  // Favicon & SEO
  favicon_url: string;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  seo_og_image: string;
  seo_author: string;
  // Admin panel theme
  admin_primary_color: string;
  admin_secondary_color: string;
  // User app theme
  app_primary_color: string;
  app_secondary_color: string;
  // Landing page texts
  landing_hero_title: string;
  landing_hero_highlight: string;
  landing_feature_1: string;
  landing_feature_2: string;
  landing_feature_3: string;
  landing_feature_4: string;
  landing_stat_1_val: string;
  landing_stat_1_label: string;
  landing_stat_2_val: string;
  landing_stat_2_label: string;
  landing_stat_3_val: string;
  landing_stat_3_label: string;
  // Contact
  support_email: string;
  finance_email: string;
  whatsapp: string;
  help_url: string;
  main_domain: string;
  site_url: string;
  privacy_policy_url: string;
  terms_url: string;
  // Financial
  default_currency: string;
  currency_format: string;
  timezone: string;
  default_trial_days: string;
  default_gateway: string;
  global_environment: string;
  // Access
  allow_public_signup: string;
  require_email_confirm: string;
  manual_approval: string;
  allow_account_delete: string;
  allow_plan_downgrade: string;
  // Security
  admin_2fa: string;
  session_expiry_hours: string;
  max_login_attempts: string;
  // System
  maintenance_mode: string;
  maintenance_message: string;
  system_version: string;
  logs_enabled: string;
  // Welcome notification
  welcome_notification_enabled: string;
  welcome_notification_title: string;
  welcome_notification_body: string;
  // PDF Document settings
  pdf_logo_url: string;
  pdf_company_name: string;
  pdf_subtitle: string;
  pdf_primary_color: string;
  pdf_footer_text: string;
  pdf_legal_note: string;
  pdf_filename_prefix: string;
  pdf_show_logo: string;
  pdf_show_summary: string;
  pdf_show_categories: string;
  pdf_show_transactions: string;
  // Google Analytics GA4
  ga4_enabled: string;
  ga4_measurement_id: string;
  ga4_environment: string;
  ga4_track_events: string;
  // Panda AI
  ai_enabled_global: string;
  ai_welcome_message: string;
  ai_button_icon_url: string;
  ai_assistant_name: string;
}

export const SETTINGS_DEFAULTS: SystemSettings = {
  platform_name: '',
  platform_slogan: '',
  logo_url: '',
  logo_small_url: '',
  // SEO defaults
  favicon_url: '',
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  seo_og_image: '',
  seo_author: '',
  admin_primary_color: '#7c3aed',
  admin_secondary_color: '#06b6d4',
  app_primary_color: '#10b981',
  app_secondary_color: '#06b6d4',
  // Landing page defaults
  landing_hero_title: 'Controle total das suas',
  landing_hero_highlight: 'finanças pessoais',
  landing_feature_1: 'Visão completa das suas finanças',
  landing_feature_2: 'Dados isolados e seguros por usuário',
  landing_feature_3: 'Metas financeiras inteligentes',
  landing_feature_4: 'Controle de cartões e faturas',
  landing_stat_1_val: '+12mil',
  landing_stat_1_label: 'usuários ativos',
  landing_stat_2_val: '4.9★',
  landing_stat_2_label: 'avaliação média',
  landing_stat_3_val: '100%',
  landing_stat_3_label: 'dados seguros',
  support_email: '',
  finance_email: '',
  whatsapp: '',
  help_url: '',
  main_domain: '',
  site_url: '',
  privacy_policy_url: '',
  terms_url: '',
  default_currency: 'BRL',
  currency_format: 'pt-BR',
  timezone: 'America/Sao_Paulo',
  default_trial_days: '7',
  default_gateway: 'stripe',
  global_environment: 'sandbox',
  allow_public_signup: 'true',
  require_email_confirm: 'false',
  manual_approval: 'false',
  allow_account_delete: 'true',
  allow_plan_downgrade: 'true',
  admin_2fa: 'false',
  session_expiry_hours: '24',
  max_login_attempts: '5',
  maintenance_mode: 'false',
  maintenance_message: 'Sistema em manutenção. Voltamos em breve.',
  system_version: '1.0.0',
  logs_enabled: 'true',
  welcome_notification_enabled: 'true',
  welcome_notification_title: 'Bem-vindo(a) à plataforma! 🎉',
  welcome_notification_body: 'Sua conta foi criada com sucesso. Explore o dashboard e comece a organizar suas finanças agora mesmo!',
  // PDF Document defaults
  pdf_logo_url: '',
  pdf_company_name: '',
  pdf_subtitle: 'Relatório Financeiro',
  pdf_primary_color: '#10b981',
  pdf_footer_text: 'Documento gerado automaticamente pelo sistema',
  pdf_legal_note: '',
  pdf_filename_prefix: 'relatorio',
  pdf_show_logo: 'true',
  pdf_show_summary: 'true',
  pdf_show_categories: 'true',
  pdf_show_transactions: 'true',
  // Google Analytics GA4
  ga4_enabled: 'false',
  ga4_measurement_id: '',
  ga4_environment: 'production',
  ga4_track_events: 'true',
  // Panda AI
  ai_enabled_global: 'false',
  ai_welcome_message: '',
  ai_button_icon_url: '',
  ai_assistant_name: 'Panda AI',
};

/** Dynamically update <head> SEO tags so they reflect system_settings without a page reload */
function applySeoMeta(s: SystemSettings) {
  // ── Helper: upsert a <meta name="..."> tag ──────────────────────────────
  const setMetaName = (name: string, content: string) => {
    if (!content) return;
    let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  // ── Helper: upsert a <meta property="..."> tag ──────────────────────────
  const setMetaProp = (property: string, content: string) => {
    if (!content) return;
    let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', property);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  // ── Browser tab title ───────────────────────────────────────────────────
  const title = s.seo_title || s.platform_name || '';
  if (title) document.title = title;

  // ── Favicon ─────────────────────────────────────────────────────────────
  const faviconHref = s.favicon_url || s.logo_small_url || s.logo_url || '';
  if (faviconHref) {
    let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/png';
    link.href = faviconHref;
  }

  // ── Standard meta tags ─────────────────────────────────────────────────
  setMetaName('description',  s.seo_description || s.platform_slogan || '');
  setMetaName('keywords',     s.seo_keywords);
  setMetaName('author',       s.seo_author || s.platform_name || '');

  // ── Open Graph ──────────────────────────────────────────────────────────
  const ogImage = s.seo_og_image || s.logo_url || '';
  const ogUrl   = s.site_url || '';
  setMetaProp('og:type',        'website');
  setMetaProp('og:title',       title);
  setMetaProp('og:description', s.seo_description || s.platform_slogan || '');
  setMetaProp('og:image',       ogImage);
  setMetaProp('og:url',         ogUrl);
  setMetaProp('og:site_name',   s.platform_name || '');

  // ── Twitter / X Cards ──────────────────────────────────────────────────
  setMetaName('twitter:card',        'summary_large_image');
  setMetaName('twitter:title',       title);
  setMetaName('twitter:description', s.seo_description || s.platform_slogan || '');
  setMetaName('twitter:image',       ogImage);
}

/** Apply branding CSS variables to document root so the whole app reacts to settings */
function applyThemeVars(s: SystemSettings) {
  const root = document.documentElement;
  root.style.setProperty('--admin-primary',   s.admin_primary_color   || '#7c3aed');
  root.style.setProperty('--admin-secondary', s.admin_secondary_color || '#06b6d4');
  root.style.setProperty('--app-primary',     s.app_primary_color     || '#10b981');
  root.style.setProperty('--app-secondary',   s.app_secondary_color   || '#06b6d4');
}

// ── Module-level cache + subscriber system ────────────────────────────────────
// This is the core of the real-time propagation mechanism.
// When refreshGlobalSettings() is called (e.g. after saving), ALL mounted
// components that use useSystemSettings() will automatically re-render.
let _cache: SystemSettings | null = null;
let _fetching = false;

// One-time resolvers waiting for the initial fetch to complete
const _pendingResolvers: Array<(s: SystemSettings) => void> = [];

// Persistent subscribers — one per mounted useSystemSettings() instance.
// When new settings arrive, every subscriber is called, triggering a re-render.
const _activeSubscribers = new Set<(s: SystemSettings) => void>();

function broadcastToAll(s: SystemSettings) {
  // Resolve one-time waiters (concurrent initial fetches)
  _pendingResolvers.splice(0).forEach((fn) => fn(s));
  // Notify all currently mounted hook instances
  _activeSubscribers.forEach((fn) => fn(s));
}

async function fetchSettings(): Promise<SystemSettings> {
  if (_cache) return _cache;
  if (_fetching) {
    // Another fetch is in progress — queue this caller
    return new Promise((resolve) => {
      _pendingResolvers.push(resolve);
    });
  }
  _fetching = true;
  try {
    const { data, error } = await supabase.from('system_settings').select('key, value');
    if (error) throw error;

    // Guard: if 0 rows returned (e.g. RLS blocked before auth loaded), do NOT cache.
    // Let the next call retry so we get real data once auth is ready.
    if (!data || data.length === 0) {
      // Still broadcast defaults so components don't hang in loading state
      broadcastToAll({ ...SETTINGS_DEFAULTS });
      return { ...SETTINGS_DEFAULTS };
    }

    const map: Record<string, string> = {};
    (data ?? []).forEach((row: { key: string; value: string }) => {
      map[row.key] = row.value ?? '';
    });
    const result: SystemSettings = { ...SETTINGS_DEFAULTS, ...map } as SystemSettings;
    _cache = result;
    applyThemeVars(result);
    applySeoMeta(result);
    broadcastToAll(result);
    return result;
  } finally {
    _fetching = false;
  }
}

/** Invalidate cache so next fetchSettings() hits the database */
export function invalidateSettingsCache() {
  _cache = null;
}

/**
 * Invalidate cache, re-fetch from DB and broadcast the new settings to ALL
 * currently mounted useSystemSettings() instances. Call this after saving.
 */
export async function refreshGlobalSettings(): Promise<SystemSettings> {
  invalidateSettingsCache();
  return fetchSettings();
}

export function useSystemSettings() {
  const [settings, setSettings] = useState<SystemSettings>(_cache ?? SETTINGS_DEFAULTS);
  const [loading, setLoading] = useState(!_cache);

  const reload = useCallback(async () => {
    setLoading(true);
    await refreshGlobalSettings();
  }, []);

  useEffect(() => {
    // Register this instance as a persistent subscriber.
    // It will be notified whenever refreshGlobalSettings() is called.
    const subscriber = (s: SystemSettings) => {
      setSettings(s);
      setLoading(false);
    };

    _activeSubscribers.add(subscriber);

    if (_cache) {
      // Data already in cache — use it immediately (no loading flicker)
      setSettings(_cache);
      setLoading(false);
    } else {
      // Trigger a fetch; broadcastToAll will call our subscriber when done
      fetchSettings().catch(() => {
        setLoading(false);
      });
    }

    // Cleanup: remove subscriber when component unmounts
    return () => {
      _activeSubscribers.delete(subscriber);
    };
  }, []);

  return { settings, loading, reload };
}
